import { Divider, IconButton, Typography } from '@mui/material'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Add, Close, Search } from '@mui/icons-material'
import { FormikProvider, useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Radio from '@mui/material/Radio'
import { isAxiosError } from 'axios'
import Box from '@mui/material/Box'

import { LocationAddressOptionType } from 'views/Authorized/views/Locations/types'
import { findCountryKeyByName } from 'shared/utils/helpers/findCountryName'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useEditCustomerMutation } from 'shared/store/useCustomers'
import { CustomerType } from 'shared/store/useCustomers/types'
import { DeliveryTypes, PaymentMethods } from 'shared/consts'
import getCompanyData from 'shared/services/getCompanyData'
import useCountriesQuery from 'shared/store/useCountries'
import SelectField from 'shared/components/SelectField'
import InputField from 'shared/components/InputField'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { ModalDetailsType } from '../../types'

interface Props {
   customer?: CustomerType
   setModalDetails: (details: ModalDetailsType) => void
}

export default function ClientData({ customer, setModalDetails }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   const { data: countriesData } = useCountriesQuery()
   const [isCompanyDataFetching, setIsCompanyDataFetching] = useState(false)
   const [isCompany, setIsCompany] = useState(true)
   const { isLoading: isEditingCustomer } = useEditCustomerMutation()

   const [options, setOptions] = useState<LocationAddressOptionType>({
      voivodeshipOptions: [],
      countyOptions: [],
   })

   const { values, setFieldValue, setValues, validateForm, setTouched, setFieldError, setErrors, handleSubmit } =
      useFormikContext<CustomerType>()

   function touchAllFields(obj: any): any {
      if (obj !== null && typeof obj === 'object' && !Array.isArray(obj)) {
         return Object.keys(obj).reduce(
            (acc, key) => {
               acc[key] = touchAllFields(obj[key])
               return acc
            },
            {} as { [key: string]: boolean | typeof obj }
         )
      }

      return true
   }
   async function handleOnSearch() {
      setIsCompanyDataFetching(true)
      await getCompanyData(values.taxID!)
         .then((res) => {
            if (res) {
               setValues(
                  {
                     ...values,
                     companyName: res.companyName,
                     displayName: res.companyName,
                     address: {
                        country: 'Polska',
                        voivodeship: res.voivodeship ?? values.address.voivodeship,
                        houseNumber: res.houseNumber ?? values.address.houseNumber,
                        county: res.county ?? values.address.county,
                        city: res.city ?? values.address.city,
                        street: res.street ?? values.address.street,
                        zipCode: res.postalCode ?? values.address.zipCode,
                     },
                  },
                  true
               )
            }
            validateForm({
               ...values,
               displayName: res.companyName,
               companyName: res.companyName,
               address: {
                  country: 'Polska',
                  voivodeship: res.voivodeship,
                  houseNumber: res.houseNumber,
                  county: res.county,
                  city: res.city,
                  street: res.street,
                  zipCode: res.postalCode,
               },
            }).then((errors) => {
               const allFieldsTouched = touchAllFields(values)
               setTouched(allFieldsTouched).then(() => {
                  setErrors(errors)
               })
            })
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               setFieldError('taxID', t(`TRANSLATION:${error.response?.data?.errors[0].code}`))
            } else {
               setFieldError('taxID', error)
            }
         })
         .finally(() => setIsCompanyDataFetching(false))
   }

   useEffect(() => {
      if (countriesData && values && values && values.address.country === 'Polska') {
         const countryKey = findCountryKeyByName(countriesData, values.address.country)
         if (countryKey) {
            const selectedCountry = countriesData[countryKey]
            if (selectedCountry) {
               setOptions({
                  voivodeshipOptions: Object.keys(selectedCountry.voivodeships).map((item) => ({
                     name: item,
                     value: item,
                  })),
                  countyOptions: values.address.voivodeship
                     ? selectedCountry.voivodeships[values.address.voivodeship].map((item) => ({
                          name: item,
                          value: item,
                       }))
                     : [],
               })
            }
         }
      }
   }, [countriesData, values, values])

   useEffect(() => {
      if (customer) {
         setValues({ ...customer })
         setIsCompany(!!customer.taxID)
      }
   }, [customer])

   return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
         <Box
            sx={{
               display: 'flex',
               py: '24px',
               flexDirection: 'column',
               minWidth: { lg: '750px', md: '500px', xs: 'auto' },
               gap: '12px',
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  gap: '12px',
                  flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                  justifyContent: 'center',
                  alignItems: 'center',
               }}
            >
               <FormControlLabel
                  onClick={() => setIsCompany(true)}
                  checked={isCompany}
                  control={<Radio />}
                  label={t('CUSTOMERS:modal.new.company')}
               />
               <FormControlLabel
                  onClick={() => setIsCompany(false)}
                  checked={!isCompany}
                  control={<Radio />}
                  label={t('CUSTOMERS:modal.new.person')}
               />
            </Box>
            {isCompany ? (
               <>
                  <Typography variant="h6">{t('CUSTOMERS:modal.new.companyData')}</Typography>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                        alignItems: 'center',
                        gap: '12px',
                     }}
                  >
                     <InputField
                        sx={{ flex: 0.33 }}
                        slotProps={{
                           input: {
                              endAdornment: (
                                 <ContainedButton
                                    disabled={isCompanyDataFetching || !values.taxID}
                                    onClick={handleOnSearch}
                                    variant="contained"
                                 >
                                    <Search />
                                 </ContainedButton>
                              ),
                           },
                        }}
                        name="taxID"
                        fullWidth
                        label={t('TRANSLATION:formFields.taxID')}
                     />

                     <InputField
                        sx={{ flex: 0.67 }}
                        name="companyName"
                        fullWidth
                        label={t('TRANSLATION:formFields.companyName')}
                     />
                  </Box>
                  <InputField fullWidth name="displayName" label="Nazwa" />
               </>
            ) : (
               <>
                  <Typography variant="h6">{t('CUSTOMERS:modal.new.clientData')}</Typography>
                  <InputField fullWidth name="displayName" label={t('CUSTOMERS:modal.new.displayName')} />

                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                        alignItems: 'center',
                        gap: '12px',
                     }}
                  >
                     <InputField name="name" fullWidth label={t('TRANSLATION:formFields.name')} />

                     <InputField name="surname" fullWidth label={t('TRANSLATION:formFields.surname')} />
                  </Box>
               </>
            )}
            <Box sx={{ display: 'flex', flexDirection: { lg: 'row', md: 'row', xs: 'column' }, gap: '12px' }}>
               <SelectField
                  searcher
                  noMinWidth
                  inputVariant="outlined"
                  fullWidth
                  name="address.voivodeship"
                  label={t('TRANSLATION:formFields.voivodeship')}
                  options={options.voivodeshipOptions}
               />
               <SelectField
                  searcher
                  fullWidth
                  noMinWidth
                  inputVariant="outlined"
                  name="address.county"
                  disabled={!values.address.voivodeship}
                  label={t('TRANSLATION:formFields.county')}
                  options={options.countyOptions}
               />
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', flexDirection: { lg: 'row', md: 'row', xs: 'column' } }}>
               <InputField name="address.zipCode" fullWidth label={t('TRANSLATION:formFields.postCode')} />
               <InputField name="address.city" fullWidth label={t('TRANSLATION:formFields.city')} />
               <InputField name="address.street" fullWidth label={t('TRANSLATION:formFields.street')} />
               <InputField name="address.houseNumber" fullWidth label={t('TRANSLATION:formFields.houseNumber')} />
            </Box>
            <Typography variant="h6">{t('CUSTOMERS:modal.new.paymentDetails')}</Typography>
            <Box
               sx={{
                  display: 'flex',
                  flexDirection: { lg: 'row', md: 'row', xs: 'column' },
                  gap: '12px',
                  alignItems: 'center',
               }}
            >
               <SelectField
                  fullWidth
                  noMinWidth
                  inputVariant="outlined"
                  name="deliveryType"
                  label={t('TRANSLATION:formFields.deliveryType')}
                  options={DeliveryTypes.map((type) => ({ value: type, name: t(`TRANSLATION:${type}`) }))}
               />
               <SelectField
                  fullWidth
                  noMinWidth
                  inputVariant="outlined"
                  name="paymentMethod"
                  label={t('TRANSLATION:formFields.paymentMethod')}
                  options={PaymentMethods.map((p) => ({ value: p, name: t(`TRANSLATION:${p}`) }))}
               />
            </Box>
            <Typography variant="h6">{t('CUSTOMERS:modal.new.deliveryAddresses')}</Typography>
            {values.deliveryAddresses.map((_, index) => (
               <>
                  <Divider flexItem>
                     <Typography>{t('CUSTOMERS:modal.new.deliveryAddress', { count: index + 1 })}</Typography>
                  </Divider>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '12px',
                        alignItems: 'center',
                     }}
                  >
                     <Box
                        flex={1}
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           gap: '12px',
                        }}
                     >
                        <Box
                           sx={{ display: 'flex', flexDirection: { lg: 'row', md: 'row', xs: 'column' }, gap: '12px' }}
                        >
                           <InputField
                              name={`deliveryAddresses[${index}].voivodeship`}
                              fullWidth
                              label={t('TRANSLATION:formFields.voivodeship')}
                           />
                           <InputField
                              name={`deliveryAddresses[${index}].county`}
                              fullWidth
                              label={t('TRANSLATION:formFields.county')}
                           />
                        </Box>
                        <Box
                           sx={{ display: 'flex', flexDirection: { lg: 'row', md: 'row', xs: 'column' }, gap: '12px' }}
                        >
                           <InputField
                              name={`deliveryAddresses[${index}].zipCode`}
                              fullWidth
                              label={t('TRANSLATION:formFields.postCode')}
                           />
                           <InputField
                              name={`deliveryAddresses[${index}].city`}
                              fullWidth
                              label={t('TRANSLATION:formFields.city')}
                           />
                           <InputField
                              name={`deliveryAddresses[${index}].street`}
                              fullWidth
                              label={t('TRANSLATION:formFields.street')}
                           />
                           <InputField
                              name={`deliveryAddresses[${index}].houseNumber`}
                              fullWidth
                              label={t('TRANSLATION:formFields.houseNumber')}
                           />
                        </Box>
                     </Box>
                     <IconButton
                        onClick={() => {
                           const updated = [...values.deliveryAddresses]
                           updated.splice(index, 1)
                           setFieldValue('deliveryAddresses', updated)
                        }}
                        sx={{ '&:hover': { color: 'red' } }}
                     >
                        <Close />
                     </IconButton>
                  </Box>
               </>
            ))}
            <IconButton
               sx={{ alignSelf: 'center' }}
               onClick={() => {
                  const newAddresses = [...values.deliveryAddresses]
                  if (values.deliveryAddresses.length > 0) {
                     newAddresses.push({
                        street: '',
                        houseNumber: '',
                        zipCode: '',
                        city: '',
                        voivodeship: '',
                        county: '',
                        country: 'Polska',
                     })
                  } else {
                     newAddresses.push({
                        street: values.address.street ?? '',
                        houseNumber: values.address.houseNumber ?? '',
                        zipCode: values.address.zipCode ?? '',
                        city: values.address.city ?? '',
                        voivodeship: values.address.voivodeship ?? '',
                        county: values.address.county ?? '',
                        country: 'Polska',
                     })
                  }
                  setFieldValue('deliveryAddresses', newAddresses)
               }}
            >
               <Add />
            </IconButton>
         </Box>
         <Box sx={{ display: 'flex', gap: '12px', m: '0 auto' }}>
            <OutlinedButton
               isDelete
               variant="outlined"
               sx={{ ml: 'auto' }}
               onClick={() =>
                  setModalDetails({
                     open: true,
                     variant: 'DELETE',
                     customerDetails: customer,
                  })
               }
            >
               {t('CUSTOMERS:deleteCustomer')}
            </OutlinedButton>
            <ContainedButton onClick={() => handleSubmit()} disabled={isEditingCustomer} variant="contained">
               {t('TRANSLATION:save')}
            </ContainedButton>
         </Box>
      </Box>
   )
}
